import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Mail, Trash2, Calendar, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const { getAuthHeader } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/contact/${messageId}`, {
        headers: getAuthHeader()
      });
      setMessages(messages.filter(msg => msg.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-messages-page">
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1e3a5f]">
          Messages
        </h1>
        <p className="text-gray-500 mt-1">{messages.length} message(s) reçu(s)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Messages List */}
        <div className="lg:col-span-1 bg-white shadow-sm overflow-hidden">
          <div className="p-4 bg-[#f8f9fa] border-b">
            <h2 className="font-semibold text-[#1e3a5f]">Boîte de réception</h2>
          </div>
          {messages.length > 0 ? (
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  onClick={() => setSelectedMessage(message)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedMessage?.id === message.id 
                      ? 'bg-[#1e3a5f]/5 border-l-4 border-[#c9a227]' 
                      : 'hover:bg-[#f8f9fa]'
                  }`}
                  data-testid={`message-item-${index}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-[#1e3a5f] text-sm truncate">
                      {message.name}
                    </span>
                    <span className="text-gray-400 text-xs whitespace-nowrap ml-2">
                      {new Date(message.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm font-medium truncate">{message.subject}</p>
                  <p className="text-gray-400 text-xs truncate mt-1">{message.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Mail className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500">Aucun message pour le moment.</p>
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selectedMessage ? (
            <div className="bg-white shadow-sm">
              <div className="p-6 border-b flex items-center justify-between">
                <div>
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#1e3a5f]">
                    {selectedMessage.subject}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    De: {selectedMessage.name} ({selectedMessage.email})
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-none"
                      data-testid="delete-message-btn"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action est irréversible. Le message sera définitivement supprimé.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none">Annuler</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleDelete(selectedMessage.id)}
                        className="bg-red-500 hover:bg-red-600 rounded-none"
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              <div className="p-6">
                {/* Contact Info */}
                <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={16} className="text-[#c9a227]" />
                    <a href={`mailto:${selectedMessage.email}`} className="hover:text-[#1e3a5f]">
                      {selectedMessage.email}
                    </a>
                  </div>
                  {selectedMessage.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={16} className="text-[#c9a227]" />
                      <a href={`tel:${selectedMessage.phone}`} className="hover:text-[#1e3a5f]">
                        {selectedMessage.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={16} className="text-[#c9a227]" />
                    {formatDate(selectedMessage.created_at)}
                  </div>
                </div>

                {/* Message Content */}
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selectedMessage.message}
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-8 pt-6 border-t flex gap-4">
                  <a href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}>
                    <Button className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none">
                      <Mail className="mr-2" size={16} />
                      Répondre par Email
                    </Button>
                  </a>
                  {selectedMessage.phone && (
                    <a href={`https://wa.me/${selectedMessage.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-none">
                        Répondre sur WhatsApp
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow-sm h-full min-h-[400px] flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Mail size={48} className="mx-auto mb-4 opacity-50" />
                <p>Sélectionnez un message pour le lire</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
